import React, { useState, useEffect } from 'react';
import { getFixedImageUrl } from '../utils/image';
import { getImageLocally } from '../utils/imageDb';

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string | undefined;
  fallback?: string;
}

export function Image({ src, fallback, alt, className, style, ...props }: ImageProps) {
  const [localUrl, setLocalUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let isMounted = true;

    async function loadLocal() {
      if (!src) return;
      
      try {
        // Checking IndexedDB for ALL images (local_ prefix or remote URLs)
        const blob = await getImageLocally(src);
        if (blob && isMounted) {
          objectUrl = URL.createObjectURL(blob);
          setLocalUrl(objectUrl);
          setError(false);
          return;
        }
      } catch (e) {
        // Ignorer l'erreur IndexedDB, on passera au fetch normal
      }
      
      if (isMounted) {
        if (src.startsWith('local_')) {
          setError(true); // C'est une image locale mais pas trouvée en BDD
        } else {
          setLocalUrl(null);
          setError(false);
        }
      }
    }

    loadLocal();

    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src]);

  if (!src) {
    return fallback ? <img src={fallback} alt={alt} className={className} style={style} {...props} /> : null;
  }

  if (error) {
    return fallback ? <img src={fallback} alt={alt} className={className} style={style} {...props} /> : (
      <div className={`bg-gray-200 flex items-center justify-center text-gray-400 ${className}`} style={style}>
        <span className="text-xs p-1 text-center">Inaccessible</span>
      </div>
    );
  }

  const finalSrc = src.startsWith('local_') ? (localUrl || '') : getFixedImageUrl(src);

  if (src.startsWith('local_') && !localUrl) {
    return <div className={`animate-pulse bg-gray-200 ${className}`} style={style} />;
  }

  return (
    <img 
      src={finalSrc} 
      alt={alt} 
      className={className} 
      style={style} 
      onError={() => setError(true)}
      {...props} 
    />
  );
}

export default Image;
